import * as React from 'react';
import { History, Location } from 'history';
import { Col, FormControl, FormGroup, MenuItem, Row, Dropdown } from 'react-bootstrap';

import { PagedList } from '../gs-core/common';

import { object as PropObject } from 'prop-types';
import { FilterParams, FilterParamsModel, TitleStat, SortTypes } from '../common/models';

import './TitlesUsedView.css';
import { TitleStatLink } from './TitleStatLink';
import { Pagination } from '../Pagination/Pagination';
import { ServiceRegistry } from '../services';

interface TitlesUsedViewProps {
  owningUnitId?: string;
  onLoading: (loading: boolean) => void;
  history: History;
  location: Location;
}

interface TitlesUsedViewState {
  url: string;
  loading: boolean;
  filter: FilterParams;
  titles?: PagedList<TitleStat>;
}

const PAGE_SIZE = 50;

export class TitlesUsedView extends React.Component<TitlesUsedViewProps, TitlesUsedViewState> {
  static contextTypes = { services: PropObject };

  constructor(props: TitlesUsedViewProps) {
    super(props);

    this.state = {
      url: `/unit/${this.props.owningUnitId}/titles`,
      loading: false,
      filter: FilterParamsModel.create({
        owningUnitId: this.props.owningUnitId,
        title: '',
        sort: SortTypes.lastUsedDate,
      }),
    };
  }

  async componentDidUpdate(prevProps: TitlesUsedViewProps) {
    if (prevProps.location !== this.props.location) {
      return this.loadTitles();
    }
  }

  async componentDidMount() {
    await this.loadTitles();
  }

  loadTitles = async () => {
    const { errorLogger, titleService } = this.context.services as ServiceRegistry;

    this.props.onLoading(true);

    try {
      const filter = FilterParamsModel.createFromQuery(this.props.location.search, {
        owningUnitId: this.props.owningUnitId,
        title: '',
      });

      this.setState({
        url: `/unit/${this.props.owningUnitId}/titles`,
        loading: true,
        filter,
      });

      const titles = await titleService.find({ ...filter, pageSize: PAGE_SIZE });

      this.setState({ titles });
    } catch (err) {
      await errorLogger.log(err);
    }

    this.setState({ loading: false });
    this.props.onLoading(false);
  };

  onSearchChange = (event: React.FormEvent<FormControl & HTMLInputElement>) => {
    const filter = FilterParamsModel.create({
      ...this.state.filter,
      title: event.currentTarget.value,
    });

    this.setState({ filter });
  };

  onSearchEnter = (event: React.KeyboardEvent<FormControl & HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const queryString = this.state.filter ? this.state.filter.toQueryString() : '';
      this.props.history.push(`${this.state.url}${queryString ? '?' : ''}${queryString}`);
    }
  };

  changeSort = (sort: any) => {
    const currentSort = (this.state.filter && this.state.filter.sort) || SortTypes.default();
    let sortDir = this.state.filter && this.state.filter.sortDir;

    if (currentSort === sort) {
      sortDir = SortTypes.toggleDir(sortDir);
    }

    const filter = FilterParamsModel.create({ ...this.state.filter, sort, sortDir });
    const queryString = filter.toQueryString();

    this.props.history.push(`${this.state.url}?${queryString}`);
  };

  changePage = (page: number) => {
    const filter = FilterParamsModel.create({ ...this.state.filter, page });
    const queryString = filter.toQueryString();

    this.props.history.push(`${this.state.url}${queryString ? '?' : ''}${queryString}`);
  };

  render() {
    const { titles, loading, filter } = this.state;
    const selectedSort = (filter && filter.sort) || SortTypes.lastUsedDate;
    const dirIcon = filter && filter.sortDir === 'asc' ? 'fa-caret-up' : 'fa-caret-down';

    if (!titles) {
      return null;
    }

    const subListSize = Math.ceil(titles.records.length / 3);

    const list1 = titles.records.slice(0, subListSize);
    const list2 = titles.records.slice(list1.length, list1.length + subListSize);
    const list3 = titles.records.slice(list1.length + list2.length, list1.length + list2.length + subListSize);

    return (
      <div className="TitlesUsedView">
        <Row className="top-controls">
          <Col xs={6}>
            <h2>
              Titles Used
              <small>
                <Dropdown id="sort">
                  <Dropdown.Toggle bsStyle="link" noCaret={true}>
                    {SortTypes.display(selectedSort)} <i className={'fa ' + dirIcon} />
                  </Dropdown.Toggle>
                  <Dropdown.Menu>
                    {SortTypes.all().map((sort) => (
                      <MenuItem key={sort} onSelect={this.changeSort} eventKey={sort} active={selectedSort === sort}>
                        {SortTypes.display(sort)} {selectedSort === sort ? <i className={'fa ' + dirIcon} /> : ''}
                      </MenuItem>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </small>
            </h2>
          </Col>
          <Col md={6}>
            <form className="form-inline pull-right">
              <FormGroup>
                <FormControl
                  type="text"
                  value={this.state.filter ? this.state.filter.title : ''}
                  placeholder="Search title"
                  onChange={this.onSearchChange}
                  onKeyDown={() => this.onSearchEnter}
                />
              </FormGroup>
            </form>
          </Col>
        </Row>

        <Row>
          {[list1, list2, list3].map((list, index) => (
            <Col
              key={`titles-list-${index}`}
              md={6}
              lg={4}
              className={`list ${index === 1 ? 'middle-list' : 'outer-list'}`}
            >
              {list.map((titleStat) => (
                <TitleStatLink key={titleStat.title} titleStat={titleStat} />
              ))}
            </Col>
          ))}
        </Row>

        <hr />

        <Row>
          {/* Elasticsearch aggregation does NOT support paging so pass total: -1 and recordCount to fake it */}
          <Pagination
            page={titles.page}
            pageSize={PAGE_SIZE}
            currentPageCount={titles.records.length}
            disabled={loading}
            onChangePage={this.changePage}
          />
        </Row>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/TitlesUsedView/TitlesUsedView.tsx